import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Navbar from '../components/Navbar';

const { FiCheck } = FiIcons;

const Home = () => {
  const formRef = useRef(null);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  // Load the external script for the form embed
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://calendrier.karinesilva.ca/js/form_embed.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="bg-[#FDF6F3] min-h-screen font-sans text-[#1A1A1A] overflow-x-hidden selection:bg-[#5c8085] selection:text-white">
      <Navbar scrollToContact={scrollToForm} />

      {/* HERO SECTION - Simplified & Form Integrated */}
      <section className="py-24 md:py-32 px-6 min-h-[90vh] flex items-center">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            {/* Left Content: Text */}
            <div className="w-full lg:w-1/2 text-left">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                {/* Scarcity Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E8D5C8]/30 border border-[#E8D5C8] text-[#8a6a5c] text-sm font-medium mb-6">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E8D5C8] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#bfa093]"></span>
                  </span>
                  3 places restantes
                </div>

                <h1 className="font-serif text-4xl md:text-6xl leading-[1.1] font-medium mb-6">
                  150+ dirigeants ont éliminé les conflits d'équipe et retrouvé la performance en 12 semaines
                </h1>

                {/* Updated Subheader */}
                <p className="text-lg text-gray-600 mb-8 max-w-xl leading-relaxed">
                  Transformez les tensions en cohésion.<br />
                  Les conflits en collaboration.<br />
                  L'épuisement en engagement.<br /><br />
                  En 8-12 semaines, avec un accompagnement personnalisé<br />
                  qui a fait ses preuves dans 150+ entreprises.
                </p>

                {/* Added Checkpoints */}
                <div className="flex flex-col gap-3 mb-8">
                  {[
                    "25+ ans d'expérience",
                    "150+ entreprises transformées",
                    "Résultats mesurables"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#5c8085]/10 flex items-center justify-center text-[#5c8085] shrink-0">
                        <SafeIcon icon={FiCheck} className="text-sm stroke-[3px]" />
                      </div>
                      <span className="text-lg font-medium text-gray-800">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right Content: Form */}
            <div ref={formRef} className="w-full lg:w-1/2">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="w-full flex flex-col justify-center bg-white rounded-[20px] shadow-sm"
                style={{ minHeight: '520px' }}
              >
                <iframe
                    src="https://calendrier.karinesilva.ca/widget/form/raGxY3oUwTUbStzs6hon"
                    style={{ width: '100%', height: '100%', minHeight: '500px', border: 'none', borderRadius: '20px' }}
                    id="inline-raGxY3oUwTUbStzs6hon" 
                    data-layout="{'id':'INLINE'}"
                    data-trigger-type="alwaysShow"
                    data-trigger-value=""
                    data-activation-type="alwaysActivated"
                    data-activation-value=""
                    data-deactivation-type="neverDeactivate"
                    data-deactivation-value=""
                    data-form-name="Team Building - Lead form"
                    data-height="485"
                    data-layout-iframe-id="inline-raGxY3oUwTUbStzs6hon"
                    data-form-id="raGxY3oUwTUbStzs6hon"
                    title="Team Building - Lead form"
                >
                </iframe>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS / CARDS SECTION */}
      <section className="py-10 px-6">
        <div className="container mx-auto">
          {/* CERTIFICATIONS ROW */}
          <div className="flex flex-col items-center">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6">Certifications et Reconnaissances</p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-14">
              <img src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://storage.googleapis.com/msgsndr/8e2Yci5T4eT9CYsi7J9E/media/694ab20f147f519bf2d5a942.jpeg" alt="Certification Logo" className="h-14 md:h-16 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300 mix-blend-multiply" />
              <img src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://storage.googleapis.com/msgsndr/8e2Yci5T4eT9CYsi7J9E/media/694ab18e264de60552b7a448.png" alt="Certification Logo" className="h-14 md:h-16 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
              <img src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://storage.googleapis.com/msgsndr/8e2Yci5T4eT9CYsi7J9E/media/694ab1907d382a44181f77b8.png" alt="Certification Logo" className="h-14 md:h-16 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="py-16 px-6">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-5xl mb-4">Ce qu'ils en disent</h2>
            <div className="h-1 w-20 bg-[#5c8085] mx-auto rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                text: "Karine Silva est véritablement une activatrice de talents et a su partager sa belle énergie positive lors de sessions de planification d’équipe de la SAF’Île. Elle a aussi apporté un grand appui aux organismes membres du réseau communautaire associatif de l’Île-du-Prince-Édouard.",
                fullText: "Karine Silva est véritablement une activatrice de talents et a su partager sa belle énergie positive lors de sessions de planification d’équipe de la SAF’Île. Elle a aussi apporté un grand appui aux organismes membres du réseau communautaire associatif de l’Île-du-Prince-Édouard lors de sessions de coaching des directions ainsi que de formation ou planification stratégique. Karine Silva a une approche très bienveillante qui fait qu’on se sent à l’aise lors des sessions avec elle et démontre sincèrement un intérêt à aider les personnes qu’elle accompagne.",
                author: "Isabelle Dasylva-Gill",
                role: "Direction"
              },
              {
                text: "Nous avons travaillé ensemble dans le cadre de la restructuration de mon organisme communautaire et la rédaction d'un plan de relance. Les résultats étaient au delà des attentes de mon équipe. De plus, elle a aussi participé à la formation du Conseil d'Administration.",
                author: "Client Satisfait",
                role: "Organisme Communautaire"
              },
              {
                text: "Karine Silva est une conférencière et formatrice exceptionnelle. Sa passion pour son travail, son engagement envers ses clients et la qualité remarquable de ses présentations témoignent de l’ingéniosité et du talent qui la distinguent.",
                author: "Partenaire Corporatif",
                role: "Formation"
              }
            ].map((review, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white p-8 rounded-[2rem] shadow-sm relative flex flex-col"
              >
                {/* Google Logo in top left (Replaces Quote Icon) */}
                <div className="mb-6">
                  <img src="https://images.seeklogo.com/logo-png/62/2/google-new-logo-png_seeklogo-622426.png" alt="Avis Google" className="w-8 h-8 object-contain" title="Avis vérifié Google" />
                </div>
                
                <p className="text-gray-600 italic mb-6 flex-grow leading-relaxed">"{review.text}"</p>
                
                <div className="mt-auto border-t border-gray-100 pt-4">
                  <p className="font-bold font-serif text-lg">{review.author}</p>
                  <p className="text-xs text-gray-400 uppercase tracking-widest">{review.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-24 px-6 bg-white rounded-t-[3rem] mt-10">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl mb-6">Est-ce que ça vous parle ?</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">Vous êtes gestionnaire et vous sentez que quelque chose ne va pas, sans avoir le temps de mettre le doigt dessus.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
            {[
              "Vos cadres sont fatigués, surchargés, débordés",
              "La motivation est en baisse dans vos équipes",
              "Il y a des tensions, des non-dits, ou des conflits",
              "La communication est difficile, même entre experts",
            ].map((item, i) => (
              <div key={i} className="flex gap-6 items-start py-6 border-b border-gray-100">
                <span className="text-3xl font-serif italic text-gray-300">0{i+1}</span>
                <p className="text-xl font-medium pt-2">{item}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center bg-[#FDF6F3] p-10 rounded-3xl">
            <p className="text-2xl font-serif italic mb-4">"Le problème n’est pas le manque d’effort."</p>
            <p className="text-lg font-bold">Le problème, c’est le manque d’alignement humain.</p>
          </div>
        </div>
      </section>

      {/* PROCESS TIMELINE */}
      <section id="approach" className="py-24 px-6 bg-[#FDF6F3]">
        <div className="container mx-auto">
          <div className="text-center mb-20">
            <h2 className="font-serif text-4xl md:text-5xl mb-4">Notre parcours de transformation</h2>
            <p className="text-gray-500">Une approche humaine, structurée et efficace en 4 étapes.</p>
          </div>

          <div className="relative max-w-6xl mx-auto">
            {/* Dashed Line Background */}
            <div className="absolute top-12 left-0 w-full h-0.5 border-t-2 border-dashed border-gray-300 hidden md:block z-0"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 relative z-10">
              {[
                { step: "1", title: "Compréhension", desc: "Analyse de vos enjeux, contexte et réalité d'équipe." },
                { step: "2", title: "Intervention", desc: "Activités de team building ciblées et stratégiques." },
                { step: "3", title: "Ancrage", desc: "Prises de conscience et leviers concrets pour le futur." },
                { step: "4", title: "Formation", desc: "Développement des compétences pour une autonomie durable." }
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-sm mb-6 border-4 border-[#FDF6F3] relative group hover:scale-110 transition-transform duration-300">
                    <span className="font-serif text-3xl font-bold">{item.step}</span>
                  </div>
                  <h3 className="font-serif text-2xl font-medium mb-3">{item.title}</h3>
                  <p className="text-gray-500 leading-relaxed px-4">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VERTICAL CARDS / SOLUTIONS */}
      <section id="results" className="py-24 px-6 bg-white rounded-[3rem] my-10 mx-4 md:mx-10 shadow-sm">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16">
            <div>
              <h2 className="font-serif text-4xl md:text-5xl mb-4">Des résultats concrets</h2>
              <p className="text-gray-500 max-w-lg">Au-delà du plaisir, nous visons un impact réel sur la dynamique de votre organisation.</p>
            </div>
            <button onClick={scrollToForm} className="mt-6 md:mt-0 px-6 py-3 border border-gray-200 rounded-full font-bold hover:bg-[#5c8085] hover:text-white transition-colors">
              Obtenez des résultats
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Motivation & Engagement", img: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800" },
              { title: "Cohésion d'équipe", img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=800" },
              { title: "Communication fluide", img: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80&w=800" }
            ].map((card, i) => (
              <div key={i} className="relative rounded-3xl overflow-hidden group h-[450px]">
                <img src={card.img} alt={card.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                {/* Black Overlay added here */}
                <div className="absolute inset-0 bg-black/40 transition-colors duration-300"></div>
                <div className="absolute inset-0 flex flex-col justify-end p-8 text-white">
                  <h3 className="font-serif text-2xl relative z-10">{card.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT / CREDIBILITY */}
      <section id="about" className="py-20 px-6">
        <div className="container mx-auto bg-[#5c8085] text-white rounded-[3rem] p-10 md:p-20 overflow-hidden relative">
          {/* Background Decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
          
          <div className="flex flex-col md:flex-row items-center gap-12 relative z-10">
            <div className="w-full md:w-1/2">
              <span className="text-[#E8D5C8] font-bold tracking-widest uppercase text-sm mb-4 block">À propos de Karine</span>
              <h2 className="font-serif text-4xl md:text-5xl mb-6 leading-tight">Remettre l'humain au cœur de la performance.</h2>
              <p style={{
      color: 'rgb(229, 231, 235)'
    }} className="text-gray-200 text-lg mb-8 leading-relaxed">
                J'accompagne les entreprises qui veulent soutenir leurs gestionnaires et créer des équipes solides, engagées et alignées. Mon approche est humaine, stratégique et adaptée à la réalité des organisations et des entreprises canadiennes.
              </p>
              <div className="flex gap-4 flex-wrap">
                <span className="px-4 py-2 bg-white/10 rounded-full text-sm">Activatrice de talents</span>
                <span className="px-4 py-2 bg-white/10 rounded-full text-sm">Stratège RH</span>
                <span className="px-4 py-2 bg-white/10 rounded-full text-sm">Coach d'équipe</span>
              </div>
            </div>
            
            <div className="w-full md:w-1/2 flex justify-center">
              <div className="relative w-64 h-80 md:w-80 md:h-96">
                <div className="absolute inset-0 border-2 border-white/20 rounded-full transform rotate-6"></div>
                <img src="https://karinesilva.ca/wp-content/uploads/2026/02/B0003327.jpg" alt="Karine Silva" className="absolute inset-0 w-full h-full object-cover rounded-full grayscale hover:grayscale-0 transition-all duration-500" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <section className="py-12 px-6 text-center bg-[#FDF6F3]">
        <div className="container mx-auto">
          <div className="flex justify-center gap-8 text-sm font-medium text-gray-400 uppercase tracking-widest">
            <span>© {new Date().getFullYear()} Karine Silva</span>
            <span>Tous droits réservés</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;